/** Small building blocks shared by every page. */
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { WEIGHT_PARTS, currencyExponent, fromMinor, parseAmount } from "@sable/domain";
import { t, tk } from "../i18n/index.ts";
import { IconX } from "./icons.tsx";
import { MarketplaceLogo } from "./MarketplaceLogo.tsx";

export function Loading() {
  return <div className="loading" role="status" aria-live="polite"><div className="spinner" /><span className="visually-hidden">{t("app.loading")}</span></div>;
}

export function ErrorBlock({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="banner banner-error row-wrap" role="alert">
      <span className="grow">{t("app.error", { message })}</span>
      {onRetry && <button className="btn btn-sm" onClick={onRetry}>{t("app.retry")}</button>}
    </div>
  );
}

export function StatusPill({ status, kind = "status" }: { status: string; kind?: "status" | "listingStatus" }) {
  return <span className={`pill pill-${status}`}>{tk(kind, status)}</span>;
}

/** The solid status tag of the inventory list; StatusPill is the quiet one for cards and panels. */
export function StatusTag({ status }: { status: string }) {
  return <span className={`tag tag-${status}`}>{tk("status", status)}</span>;
}

export function Field({ label, hint, children, optional }: { label: string; hint?: string; children: ReactNode; optional?: boolean }) {
  return (
    <label className="field">
      <span>
        {label}
        {optional && <span className="hint"> · {t("app.optional")}</span>}
      </span>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

function currencySymbol(currency: string, locale: string): string {
  const part = new Intl.NumberFormat(locale, { style: "currency", currency, currencyDisplay: "narrowSymbol" })
    .formatToParts(0).find((p) => p.type === "currency");
  return part?.value ?? currency;
}

/**
 * An amount typed in the currency's major unit, held as minor units. Keeps
 * the text the person typed while they type, and re-renders from the value
 * when it changes elsewhere.
 */
export function MoneyInput({ value, onChange, currency, locale = "en-US", id, placeholder, required }: {
  value: number | null;
  onChange: (minor: number | null) => void;
  currency: string;
  locale?: string;
  id?: string;
  placeholder?: string;
  required?: boolean;
}) {
  const shown = (v: number | null) => (v === null ? "" : fromMinor(v, currency).toFixed(currencyExponent(currency)));
  const [text, setText] = useState(shown(value));
  const last = useRef(value);
  useEffect(() => {
    if (value !== last.current) {
      setText(shown(value));
      last.current = value;
    }
  });
  return (
    <div className="money-input">
      <span className="sym">{currencySymbol(currency, locale)}</span>
      <input
        id={id}
        className="input num"
        inputMode="decimal"
        autoComplete="off"
        placeholder={placeholder ?? "0.00"}
        required={required}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const minor = parseAmount(e.target.value, currency);
          last.current = minor;
          onChange(minor === null ? null : Math.max(0, minor));
        }}
        onBlur={() => setText(shown(last.current))}
      />
    </div>
  );
}

export function Dialog({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.querySelector<HTMLElement>("input, select, textarea, button")?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <div className="dialog-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <div className="dialog-head">
          <h2 id={titleId}>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t("app.close")}><IconX /></button>
        </div>
        {children}
        {footer && <div className="dialog-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Empty({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <h2>{title}</h2>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

/**
 * A weight as two fields: pounds and ounces (kilograms and grams in a metric
 * workspace). Either or both: 35 oz, 1.3 lbs, 1 lb 10 oz. The parent converts
 * with gramsFromParts when it saves.
 */
export interface WeightParts {
  major: string;
  minor: string;
}

export function WeightFields({ label, value, onChange, units }: {
  label: string;
  value: WeightParts;
  onChange: (v: WeightParts) => void;
  units: "imperial" | "metric";
}) {
  const [big, small] = WEIGHT_PARTS[units];
  return (
    <div className="weight-input">
      <label className="unit-field">
        <input className="input num" inputMode="decimal" autoComplete="off" aria-label={`${label} (${big})`}
          value={value.major} onChange={(e) => onChange({ ...value, major: e.target.value })} />
        <span>{big}</span>
      </label>
      <label className="unit-field">
        <input className="input num" inputMode="decimal" autoComplete="off" aria-label={`${label} (${small})`}
          value={value.minor} onChange={(e) => onChange({ ...value, minor: e.target.value })} />
        <span>{small}</span>
      </label>
    </div>
  );
}

/** A marketplace's mark at row size, beside its name in panels. */
export function MarketplaceBadge({ code }: { code: string }) {
  return <MarketplaceLogo code={code} size={32} />;
}

/** A marketplace's mark at the size of a dense row. */
export function MarketplaceMark({ code }: { code: string }) {
  return <MarketplaceLogo code={code} size={22} />;
}
